import { useState } from 'react';
import { useQuery, type QueryKey } from '@tanstack/react-query';

/** The live poll's cadence: every surface reads every 2 s (TECH-867 § Decided "Updates within 3 seconds"). */
export const LIVE_POLL_INTERVAL_MS = 2_000;

/** "Reconnecting…" shows after this many failed reads in a row (TECH-867 § Errors "Bad wifi"). */
export const LIVE_RECONNECTING_AFTER = 3;

export interface UseLiveQueryPollingOptions<T> {
  queryKey: QueryKey;
  read: (signal: AbortSignal) => Promise<T>;
  enabled?: boolean;
  /** True once an answer can no longer change (a dead link, a removed poll): polling stops. */
  isSettled?: (value: T) => boolean;
  /**
   * Keep reading while the tab is hidden: the projector's screen only (its window is often not the focused
   * one). Phones and the host view stop in a hidden tab and read again the moment they are back.
   */
  pollInBackground?: boolean;
  /** A slower cadence for some values (ms), e.g. an ended poll on a feed card; the 2 s otherwise. */
  intervalFor?: (value: T) => number | undefined;
  /** True for a failure that no read can fix (a 404, a 403): polling stops, focus and reconnect included. */
  stopsOn?: (error: unknown) => boolean;
  /** Extra `meta` for `queryCacheOnError` (for example the segment of the key to redact from logs). */
  meta?: Record<string, unknown>;
}

export interface LiveQueryPolling<T> {
  data: T | undefined;
  isPending: boolean;
  /** Failed reads since the last good one. */
  failures: number;
  /** After 3 failed reads in a row; clears on the next good one. The last data stays. */
  reconnecting: boolean;
  /** The last read failed with an error `stopsOn` says no read can fix: no more reads, the last data stays. */
  stopped: boolean;
}

/**
 * The one polling read behind every live surface (the big screen, the host view, the phones' sheet).
 *
 * - Every 2 s (in a hidden tab only with `pollInBackground`, the projector's screen), and again the moment
 *   focus or the network comes back.
 * - `retry: false`: the next tick is the retry, so three failed ticks are three failures, about 6 s.
 * - `networkMode: 'always'`: a browser that believes it is offline still reads, so a dead wifi counts as
 *   failures and shows "Reconnecting…" instead of freezing the last state with no word.
 * - Never the maintenance takeover: the `read`s (`@api-client/liveReads`) are bare fetches that throw a
 *   status-less `LiveReadError`, which `queryCacheOnError` does not forward to the probe, and
 *   `suppressProbeWhenDataPresent` keeps a failure over a painted screen at warning level.
 *
 * The failure count lives with the reader that runs the reads (one per screen: the host view's preview takes
 * the host read's state as props), set from the read itself, so a failed read over unchanged data still
 * re-renders.
 */
export function useLiveQueryPolling<T>({
  queryKey,
  read,
  enabled = true,
  isSettled,
  pollInBackground = false,
  intervalFor,
  stopsOn,
  meta,
}: UseLiveQueryPollingOptions<T>): LiveQueryPolling<T> {
  const stopped = (q: { state: { status: string; error: unknown } }) =>
    q.state.status === 'error' && stopsOn?.(q.state.error) === true;
  const [failures, setFailures] = useState(0);
  const query = useQuery<T>({
    queryKey,
    enabled,
    queryFn: async ({ signal }) => {
      try {
        const value = await read(signal);
        setFailures(0);
        return value;
      } catch (error) {
        // An abort is the reader leaving (unmount, a newer read), not a failed poll.
        if (!signal.aborted) setFailures(n => n + 1);
        throw error;
      }
    },
    refetchInterval: q => {
      if (stopped(q)) return false;
      const data = q.state.data;
      if (data === undefined) return LIVE_POLL_INTERVAL_MS;
      if (isSettled?.(data)) return false;
      return intervalFor?.(data) ?? LIVE_POLL_INTERVAL_MS;
    },
    refetchIntervalInBackground: pollInBackground,
    refetchOnWindowFocus: q => (stopped(q) ? false : 'always'),
    refetchOnReconnect: q => (stopped(q) ? false : 'always'),
    staleTime: 0,
    retry: false,
    networkMode: 'always',
    meta: { suppressProbeWhenDataPresent: true, ...meta },
  });

  return {
    data: query.data,
    isPending: query.isPending,
    failures,
    reconnecting: failures >= LIVE_RECONNECTING_AFTER,
    stopped: query.status === 'error' && stopsOn?.(query.error) === true,
  };
}
